import { Link } from 'react-router-dom';
import { SidebarTrigger } from '../../components/ui/sidebar';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Loader2, PackageOpen, ExternalLink } from 'lucide-react';
import { useParkedTasks } from '../../hooks/useParkedTasks';
import {
  unparkDeepLink,
  type ListParkedTasksResult,
  type ParkedTask,
} from '../../services/parkedTask.service';

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export default function ParkedTasks() {
  const { data, isLoading, error } = useParkedTasks();
  const tasks = data?.tasks ?? [];

  return (
    <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: '100vh' }}>
      <div className="flex items-center justify-between w-full px-6 py-4 border-b border-border bg-background flex-shrink-0">
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <SidebarTrigger className="md:hidden h-5 w-5" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/develop">Develop</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/develop/tasks">Tasks</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>Parked</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          <h1 className="text-xl font-semibold text-foreground">Parked tasks</h1>
          <p className="text-sm text-muted-foreground">
            Tasks a teammate parked mid-flight. Unpark opens the task in Jimi and resumes it.
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden p-8 min-h-0">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading parked tasks…</p>
            </div>
          </div>
        ) : error ? (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-destructive text-sm">Failed to load parked tasks.</p>
          </div>
        ) : tasks.length === 0 ? (
          <div className="p-8 text-center">
            <PackageOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No parked tasks right now.</p>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[30%]">Task</TableHead>
                  <TableHead className="w-[20%]">Branch</TableHead>
                  <TableHead className="w-[16%]">Parked by</TableHead>
                  <TableHead className="w-[18%]">When</TableHead>
                  <TableHead className="w-[16%] text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map((task) => (
                  <ParkedTaskRow key={task.id} task={task} meta={data} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

function ParkedTaskRow({
  task,
  meta,
}: {
  task: ParkedTask;
  meta: ListParkedTasksResult | undefined;
}) {
  const title = task.task_name?.trim() || task.branch_name;
  const description =
    task.description && task.description.length > 110
      ? `${task.description.slice(0, 110)}…`
      : task.description;
  const href = meta ? unparkDeepLink(meta.stack, meta.tenant_slug, task.id) : undefined;

  return (
    <TableRow>
      <TableCell className="font-medium">
        <div className="truncate" title={title}>
          {title}
        </div>
        {description && (
          <div className="text-xs text-muted-foreground truncate mt-0.5" title={task.description ?? ''}>
            {description}
          </div>
        )}
      </TableCell>
      <TableCell>
        <span className="text-sm text-muted-foreground truncate" title={task.branch_name}>
          {task.branch_name}
        </span>
      </TableCell>
      <TableCell>
        <span className="text-sm text-muted-foreground truncate" title={task.created_by_label}>
          {task.created_by_label}
        </span>
      </TableCell>
      <TableCell>
        <span className="text-sm text-muted-foreground">{formatWhen(task.created_at)}</span>
      </TableCell>
      <TableCell className="text-right">
        <Button variant="default" size="sm" asChild disabled={!href}>
          <a href={href}>
            <PackageOpen className="h-4 w-4 mr-1.5" />
            Unpark in Jimi
            <ExternalLink className="h-3.5 w-3.5 ml-1.5 opacity-70" />
          </a>
        </Button>
      </TableCell>
    </TableRow>
  );
}
